"use client";

import type { CSSProperties } from "react";
import type { AppLocale, FunnelStageConfig } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { formatFunnelNumber, funnelText, stageTypeLabel } from "@/lib/funnel-planner/i18n";

const STAGE_COLORS: Record<string, string> = {
  "blog-post":       "#3b82f6",
  "landing-page":    "#10b981",
  "opt-in-page":     "#8b5cf6",
  "email-sequence":  "#f59e0b",
  "discovery-call":  "#0ea5e9",
  "proposal":        "#6366f1",
  "sales-page":      "#ef4444",
  "order-form":      "#22c55e",
  "upsell":          "#f97316",
  "webinar":         "#a855f7",
  "application":     "#14b8a6",
  "retainer-close":  "#16a34a",
  "custom":          "#6b7280",
};

const STAGE_ICONS: Record<string, string> = {
  "blog-post":       "BP",
  "landing-page":    "LP",
  "opt-in-page":     "OP",
  "email-sequence":  "EM",
  "discovery-call":  "DC",
  "proposal":        "PR",
  "sales-page":      "SP",
  "order-form":      "OF",
  "upsell":          "UP",
  "webinar":         "WB",
  "application":     "AP",
  "retainer-close":  "RC",
  "custom":          "ST",
};

function stageColor(type: string | undefined): string {
  return STAGE_COLORS[type ?? "custom"] ?? "#6b7280";
}

function stageIcon(type: string | undefined): string {
  return STAGE_ICONS[type ?? "custom"] ?? "⚙️";
}

function stageLabel(type: string | undefined): string {
  if (!type) return "Custom";
  return type.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

type Props = {
  stages: FunnelStageConfig[];
  volumes: number[];
  /** Compact=true for Step 5 preview; false (default) for success screen */
  compact?: boolean;
  content: FunnelPlannerContent;
  locale: AppLocale;
};

type FlowDiagramStyle = CSSProperties & {
  "--fp-flow-node-width": string;
  "--fp-flow-node-min-height": string;
  "--fp-flow-arrow-gap": string;
};

export function FunnelFlowDiagram({ stages, volumes, compact = false, content, locale }: Props) {
  const nodeW = compact ? 128 : 156;
  const nodeH = compact ? 78 : 90;
  const arrowGap = compact ? 36 : 48;
  const flowStyle: FlowDiagramStyle = {
    "--fp-flow-node-width": `${nodeW}px`,
    "--fp-flow-node-min-height": `${nodeH}px`,
    "--fp-flow-arrow-gap": `${arrowGap}px`,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  };

  if (stages.length === 0) return null;

  return (
    <div
      className={compact ? "funnel-flow-diagram funnel-flow-diagram--compact" : "funnel-flow-diagram"}
      data-funnel-flow-diagram="true"
      data-funnel-flow-compact={compact ? "true" : "false"}
      style={flowStyle}
    >
      <div
        className="funnel-flow-diagram__track"
        role="img"
        aria-label={funnelText(content, "flowAriaLabel", "Funnel flow: {stages}", {
          stages: stages.map((s) => s.name || funnelText(content, "flowUnnamedStage", "Unnamed")).join(" -> "),
        })}
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "nowrap",
          width: "max-content",
          paddingBottom: 4,
        }}
      >
        {stages.map((stage, i) => {
          const color = stageColor(stage.stageType);
          const icon = stageIcon(stage.stageType);
          const label = stageTypeLabel(content, stage.stageType, stageLabel(stage.stageType));
          const volume = volumes[i] ?? 0;
          const nextStage = stages[i + 1];
          const isLast = i === stages.length - 1;

          return (
            <div
              key={stage.id}
              className="funnel-flow-diagram__item"
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              {/* Node card */}
              <div
                className="funnel-flow-diagram__node"
                style={{
                  width: "var(--fp-flow-node-width)",
                  minHeight: "var(--fp-flow-node-min-height)",
                  borderRadius: 8,
                  border: "1px solid",
                  borderColor: color + "33",
                  overflow: "hidden",
                  backgroundColor: "var(--fp-card-surface, rgba(15,23,42,0.96))",
                  boxShadow: "0 14px 30px rgba(2,6,23,0.24)",
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                }}
              >
                {/* Color top strip */}
                <div style={{ height: 4, backgroundColor: color, flexShrink: 0 }} />

                {/* Card body */}
                <div
                  style={{
                    padding: compact ? "6px 8px 7px" : "8px 10px 9px",
                    display: "flex",
                    flexDirection: "column",
                    gap: compact ? 3 : 5,
                    flex: 1,
                  }}
                >
                  {/* Type label */}
                  <div
                    style={{
                      fontSize: compact ? 9 : 10,
                      color: "#94a3b8",
                      lineHeight: 1.2,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <span role="presentation">{icon}</span>
                      <span
                        className="funnel-flow-diagram__type-label"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "calc(var(--fp-flow-node-width) - 28px)",
                        }}
                      >
                        {label}
                    </span>
                  </div>

                  {/* Stage name */}
                  <div
                    className="funnel-flow-diagram__stage-name"
                    style={{
                      fontSize: compact ? 11 : 13,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: "var(--fp-text, #e2e8f0)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={stage.name || funnelText(content, "flowUnnamedStage", "Unnamed")}
                  >
                    {stage.name || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>{funnelText(content, "flowUnnamedStage", "Unnamed")}</span>}
                  </div>

                  {/* Volume badge */}
                  <div
                    className="funnel-flow-diagram__volume"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: color + "18",
                      color: color,
                      borderRadius: 4,
                      padding: compact ? "1px 5px" : "2px 6px",
                      fontSize: compact ? 9 : 10,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.4,
                      alignSelf: "flex-start",
                    }}
                  >
                    {formatFunnelNumber(volume, locale)}<span style={{ fontWeight: 400, opacity: 0.7 }}>{funnelText(content, "perMonthLabel", "/mo")}</span>
                  </div>
                </div>
              </div>

              {/* Arrow connector (not after last node) */}
              {!isLast && (
                <div
                  className="funnel-flow-diagram__connector"
                  style={{
                    width: "var(--fp-flow-arrow-gap)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    flexShrink: 0,
                    paddingTop: 2,
                  }}
                >
                  {nextStage?.isEditable && (
                    <span
                      className="funnel-flow-diagram__conversion"
                      style={{
                        fontSize: compact ? 9 : 10,
                        fontWeight: 600,
                        color: "#64748b",
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1,
                      }}
                    >
                      {nextStage.conversionRate % 1 === 0
                        ? nextStage.conversionRate.toFixed(0)
                        : nextStage.conversionRate.toFixed(1)}
                      %
                    </span>
                  )}
                  <svg
                    width={arrowGap}
                    height={14}
                    viewBox={`0 0 ${arrowGap} 14`}
                    fill="none"
                    aria-hidden
                  >
                    <line
                      x1={4}
                      y1={7}
                      x2={arrowGap - 9}
                      y2={7}
                      stroke="#475569"
                      strokeWidth={1.5}
                    />
                    <path
                      d={`M ${arrowGap - 11} 4 L ${arrowGap - 4} 7 L ${arrowGap - 11} 10`}
                      stroke="#475569"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
